import React from "react";
import ContactUs from "../../../components/ContactUs.js";
import { Helmet } from "react-helmet-async";

const services = [
  {
    logo_images: "../assets/icons/cicd/service-1.png",
    title: "Build Automation",
    description:
      "Create fully automated build pipelines with dependency caching, artifact versioning, and language-specific optimizations for containerized and legacy workloads.",
  },
  {
    logo_images: "../assets/icons/cicd/service-2.png",
    title: "Testing Automation",
    description:
      "Integrate unit, integration, security, and performance tests into CI pipelines with quality gates, parallel execution, and automated feedback loops.",
  },
  {
    logo_images: "../assets/icons/cicd/service-3.png",
    title: "Deployment Orchestration",
    description:
      "Implement blue/green, canary, and rolling deployments across Kubernetes, serverless, and VM-based environments with automated rollback strategies.",
  },
  {
    logo_images: "../assets/icons/cicd/service-4.png",
    title: "Multi-Environment Promotion",
    description:
      "Design pipelines that promote artifacts across dev, QA, staging, and production using approvals, change windows, and compliance checkpoints.",
  },
  {
    logo_images: "../assets/icons/cicd/service-5.png",
    title: "Pipeline Observability",
    description:
      "Provide real-time insights into pipeline health, lead time, and failure trends with custom dashboards and alerts across Jenkins, GitHub Actions, or GitLab.",
  },
];

const benefits = [
  "Shorter release cycles with automated quality and security gates",
  "Consistent deployments across hybrid and multi-cloud estates",
  "Improved developer productivity via standardized workflows",
  "Rapid feedback loops that catch issues before production",
  "Compliance and audit readiness with traceable change histories",
  "Reduced operational risk through automated rollback and recovery",
];

const expertise = [
  "CI/CD platform specialists across Jenkins, GitHub Actions, GitLab, and Azure DevOps",
  "Golden pipeline blueprints for microservices, data, and mobile workloads",
  "Policy-as-code enforcement using Open Policy Agent and Sentinel",
  "Command of container orchestration, serverless, and edge deployment targets",
  "Integration with Terraform, Helm, ArgoCD, and service mesh ecosystems",
  "Enablement programs and DevOps coaching for product teams",
];

const useCases = [
  {
    title: "Enterprise Release Factory",
    description:
      "Unified 150+ applications under standardized CI/CD pipelines with automated testing and gating, reducing mean time to deploy from days to under one hour.",
  },
  {
    title: "Regulated Deployment Flow",
    description:
      "Built CI/CD pipelines with segregation of duties, electronic approvals, and tamper-proof audit logs to meet SOX and HIPAA controls.",
  },
  {
    title: "Hybrid Cloud Delivery",
    description:
      "Delivered pipelines that deploy microservices to EKS and on-premise OpenShift clusters with synchronized versioning and automated rollbacks.",
  },
  {
    title: "Data & ML Pipelines",
    description:
      "Implemented CI/CD for machine learning models with automated validation, model registry integration, and controlled production rollout.",
  },
];

const reasons = [
  "Experienced DevOps engineers with end-to-end delivery ownership",
  "Reusable accelerators for pipeline-as-code and environment promotion",
  "Security-first approach embedding SAST, DAST, and dependency scans",
  "Close alignment with platform, security, and compliance stakeholders",
  "Flexible engagement models: assessments, implementations, and managed services",
  "24/7 operational support with SLO-driven monitoring and alerting",
];

const scrollToContact = () => {
  document
    .getElementById("contact-section")
    ?.scrollIntoView({ behavior: "smooth" });
};

const CICDPipelines = () => (
  <>
    <Helmet>
      <meta
        name="description"
        content="Accelerate software delivery with expert CI/CD Implementation & Consulting. Automate builds, reduce errors, and deploy faster. Get your custom strategy now."
      />
      <meta name="copyright" content="codeglim" />
      <meta
        property="og:title"
        content="End-to-End CI/CD Implementation & Consulting | Ayanshtech Solutions"
      />
      <meta property="og:site_name" content="Ayanshtech Solutions" />
      <meta
        property="og:url"
        content="http://ayanshtechnology.com/devops-cicd"
      />
      <meta
        property="og:description"
        content="Accelerate software delivery with expert CI/CD Implementation & Consulting. Automate builds, reduce errors, and deploy faster. Get your custom strategy now."
      />
      <meta property="og:type" content="website" />
      <meta
        property="og:image"
        content="http://ayanshtechnology.com/assets/logo.png"
      />

        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@Ayanshtech Solutions" />
        <meta
          name="twitter:title"
          content="End-to-End CI/CD Implementation & Consulting | Ayanshtech Solutions"
        />
        <meta
          name="twitter:description"
          content="Accelerate software delivery with expert CI/CD Implementation & Consulting. Automate builds, reduce errors, and deploy faster. Get your custom strategy now."
        />
        <meta
          name="twitter:image"
          content="http://ayanshtechnology.com/assets/logo.png"
        />
      <link rel="canonical" href="http://ayanshtechnology.com/devops-cicd" />
      <title>
       End-to-End CI/CD Implementation & Consulting | Ayanshtech Solutions
      </title>
    </Helmet>

    <div className="flex flex-col items-center justify-center mb-6 p-6">
      <section className="py-5 w-full">
        <div className="flex flex-col lg:flex-row items-center lg:space-x-16">
          <div className="lg:w-3/4 lg:text-left p-4">
            <h1 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
              End-to-End CI/CD Implementation & Consulting Services in India
            </h1>
            <p className="sm:text-base md:text-lg lg:text-lg mb-3">
              Deliver software faster and safer with <a className="  relative   
  before:content-[''] before:absolute before:left-1/2 before:-bottom-1 
  before:h-[2px] before:w-0 before:bg-green-600 
  before:transition-all before:duration-300 
  hover:before:left-0 hover:before:w-full text-green-600 font-bold" href="https://ayanshtechnology.com/devops-aws">CI/CD pipelines</a> Consulting Services in India engineered
              for modern microservices, data platforms, and legacy estates. We
              codify build, test, and deployment workflows that power continuous
              delivery at scale.
            </p>
            <button
              onClick={scrollToContact}
              className="bg-green-600 text-white hover:bg-yellow-500 hover:text-green-700 hover:border hover:border-yellow-500 font-semibold py-2 px-4 rounded-full mt-6"
            >
              Design Your Pipeline Strategy
            </button>
          </div>
          <div className="flex justify-center mb-12 lg:mb-0">
            <img
              src="../assets/images/devops-pages/cicd-banner.png"
              alt="CI/CD Pipelines"
              className="w-full h-auto sm:w-3/4 md:w-2/3 lg:w-full object-contain"
            />
          </div>
        </div>
      </section>

      {/* <section className="mb-5 mt-10 p-4 w-full">
        <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
          About Continuous Integration & Delivery
        </h2>
        <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
          CI/CD practices empower teams to ship code confidently through
          automated validation, packaging, and releases. With pipeline-as-code,
          organizations gain traceability, repeatability, and resilience.
        </p>
      </section> */}

      <section className="text-center mb-5 mt-12 w-full">
        <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
          CI/CD Services
        </h2>
        <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
          Comprehensive offerings spanning build automation, testing, releases,
          and multi-environment orchestration.
        </p>
      </section>
      <div className="rounded-3xl p-6 w-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service) => (
            <div
              key={service.title}
              className="relative flex flex-col space-y-4 rounded-3xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
             
            >
              <div className="flex items-center gap-2">
                <div className="w-[60px] h-[60px]  ">
                  <img src={service.logo_images} className="w-full" alt="" />
                </div>
                <h3 className="text-lg font-bold">{service.title}</h3>
              </div>
              <p className="text-lg text-gray-700">{service.description}</p>
            </div>
          ))}
        </div>
      </div>

      <section className="text-center mb-5 mt-20 w-full">
        <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
          Benefits of CI/CD Pipelines
        </h2>
      </section>
      <div className="p-4 w-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit) => (
            <div
              key={benefit}
              className="flex items-start space-x-4 rounded-2xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
            >
              <div className="h-3 w-3 rounded-full bg-blue-600 mt-2 flex-shrink-0" />
              <p className="text-lg text-gray-700">{benefit}</p>
            </div>
          ))}
        </div>
      </div>

      <section className="p-6 sm:px-8 md:px-12 lg:px-16 w-full mt-10 ">
        <div className="text-center mb-8">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Our CI/CD Expertise
          </h2>
        </div>
        <div className="flex justify-center flex-col md:flex-row item-center"> 
        <ul className="list-disc pl-8 sm:text-base md:text-lg lg:text-xl max-w-6xl mx-auto space-y-3 text-gray-700">
          {expertise.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
        <div className="w-full md:w-1/3 lg:w-[40%]">
          <img src="../assets/images/devops-pages/CICD-images.png" alt="" />
        </div>
        </div>
      </section>

      <section className="text-center mb-5 mt-10 w-full">
        <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
          Use Cases
        </h2>
      </section>
      <div className="rounded-3xl p-6 w-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
          {useCases.map((useCase) => (
            <div
              key={useCase.title}
              className="relative flex flex-col space-y-4 rounded-2xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
              
            >
              <h3 className="text-lg font-bold text-gray-900">
                {useCase.title}
              </h3>
              <p className="text-lg text-gray-700">{useCase.description}</p>
            </div>
          ))}
        </div>
      </div>

      <section className="text-center mb-5 mt-10 w-full">
        <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
          Why Choose Ayanshtech
        </h2>
      </section>
      <div className="p-6 sm:p-8 md:p-12 lg:p-16 w-full ">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {reasons.map((reason) => (
            <div
              key={reason}
              className="flex items-start space-x-4 rounded-2xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
            >
              <div className="h-3 w-3 rounded-full bg-green-600 mt-2 flex-shrink-0" />
              <p className="text-lg text-gray-700">{reason}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-8">
          <button
            onClick={scrollToContact}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-full text-lg transition duration-300"
          >
            Accelerate Your Delivery
          </button>
        </div>
      </div>

      <div id="contact-section" className="w-full mt-12">
        <ContactUs />
      </div>
    </div>
  </>
);

export default CICDPipelines;

